import { useId } from 'react'

/**
 * FormField — a11y primitive for form inputs (A11Y-009 from the
 * 2026-05-03 audit). Wraps any input/textarea/select via a render-prop
 * `children` so callers keep their own styling, while the primitive owns:
 *   - <label htmlFor> wired by useId() (visible OR sr-only)
 *   - aria-invalid on the input when error is set
 *   - aria-describedby pointing at the error AND/OR hint <p> elements
 *   - role="alert" on the error <p> so screen readers auto-announce
 *   - aria-required when required=true
 *   - red asterisk for required (aria-hidden so SR uses aria-required only)
 *
 * Why this matters: today most forms set a single `error` string and
 * render it loosely above the form. SR users tab-cycling back to a
 * field after a submit error don't hear which field failed because the
 * input has no aria-describedby tying the message to the control.
 * FormField fixes that pattern at the primitive level so every form
 * that adopts it gets correct error announcements for free.
 *
 *   <FormField label="Email" srLabel error={emailError} required>
 *     {props => (
 *       <input
 *         {...props}
 *         type="email"
 *         autoComplete="email"
 *         value={email}
 *         onChange={e => setEmail(e.target.value)}
 *         className="..."
 *         placeholder="Email address"
 *       />
 *     )}
 *   </FormField>
 *
 * For form-level errors (e.g. "incorrect password" returned by the
 * server) keep a separate `<p role="alert">` block below the form —
 * those aren't tied to any single field and shouldn't pretend to be.
 */
export default function FormField({
  label,
  error,
  hint,
  children,
  srLabel = false,
  required = false,
}) {
  const inputId = useId()
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`
  const describedBy = [error && errorId, hint && hintId].filter(Boolean).join(' ') || undefined
  return (
    <div className="w-full">
      <label htmlFor={inputId} className={srLabel ? 'sr-only' : 'block text-sm font-medium mb-1'}>
        {label}
        {required && (
          <span aria-hidden="true" className="text-[var(--feedback-negative)] ml-0.5">
            *
          </span>
        )}
      </label>
      {children({
        id: inputId,
        'aria-invalid': error ? 'true' : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })}
      {hint && (
        <p id={hintId} className="text-xs text-[var(--text-muted)] mt-1">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-[var(--feedback-negative)] text-sm mt-1">
          {error}
        </p>
      )}
    </div>
  )
}
